import { Form, Formik } from "formik";
import { useState } from "react";
import { Alert } from "@/core/components/ui/alert/alert";
import { AutoComplete } from "@/core/components/ui/autocomplete/autocomplete";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { TypeableDatePicker } from "@/core/components/ui/typeable-date-picker";
import { Typography } from "@/core/components/ui/typography";
import type { IEnrichedBusiness } from "@/business/business";
import { extractErrorMessage } from "@/core/api/errors";
import type { OnboardingStepPane } from "@/onboarding/routes";
import {
  buildSchedulePayload,
  getInitialBusinessSetupValues,
  needsReferenceDate,
  needsStartDay,
  PAY_PERIOD_CADENCE_LABEL,
  PAY_PERIOD_CADENCE_OPTIONS,
  START_DAY_OF_WEEK_OPTIONS,
  START_DAY_OF_WEEK_LABEL,
  validateScheduleStep,
} from "./business-setup";
import { FieldError } from "./field-error";
import { useSaveSimpleBusinessSetup } from "./query";
import { StepLayout } from "./step-layout";

interface ScheduleStepScreenProps {
  business: Pick<
    IEnrichedBusiness,
    "id" | "name" | "time_zone_label" | "current_pay_period_template"
  >;
  activePane: OnboardingStepPane;
  onContinue: () => void;
  onBack: () => void;
}

export const ScheduleStepScreen = ({
  business,
  activePane,
  onContinue,
  onBack,
}: ScheduleStepScreenProps) => {
  const initialValues = getInitialBusinessSetupValues(business);
  const [cadenceSearch, setCadenceSearch] = useState(
    initialValues.cadence
      ? PAY_PERIOD_CADENCE_LABEL[initialValues.cadence]
      : "",
  );
  const [startDaySearch, setStartDaySearch] = useState(
    initialValues.startDayOfWeek
      ? START_DAY_OF_WEEK_LABEL[initialValues.startDayOfWeek]
      : "",
  );
  const saveMutation = useSaveSimpleBusinessSetup(business.id);

  return (
    <StepLayout
      activePane={activePane}
      title="How do you pay your team?"
      subtitle="This is what your pay periods get built from, so it is worth getting exactly right."
    >
      <Formik
        initialValues={initialValues}
        validate={validateScheduleStep}
        onSubmit={(values, helpers) => {
          helpers.setStatus(undefined);
          saveMutation.mutate(buildSchedulePayload(values), {
            onSuccess: () => {
              helpers.setSubmitting(false);
              onContinue();
            },
            onError: (error) => {
              helpers.setStatus(extractErrorMessage(error).message);
              helpers.setSubmitting(false);
            },
          });
        }}
      >
        {({
          values,
          errors,
          status,
          submitCount,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form noValidate>
            <Stack gap="lg" align="stretch" fullWidth>
              <Card className="w-full border-stroke-tertiary shadow-sm">
                <CardContent className="p-6 sm:p-8">
                  <Stack gap="lg" align="stretch" fullWidth>
                    <Stack gap="xs" fullWidth>
                      <AutoComplete
                        label="Pay schedule"
                        placeholder="Select a pay schedule"
                        required
                        selectedValue={values.cadence}
                        onSelectedValueChange={(value) => {
                          setFieldValue("cadence", value);
                          // Answers to the old schedule must not carry into the new one.
                          setFieldValue("startDayOfWeek", "");
                          setFieldValue("periodStartReferenceDate", null);
                          setStartDaySearch("");
                        }}
                        searchValue={cadenceSearch}
                        onSearchValueChange={setCadenceSearch}
                        error={submitCount > 0 ? errors.cadence : undefined}
                        items={PAY_PERIOD_CADENCE_OPTIONS}
                        fullWidth
                      />
                      <FieldError
                        message={submitCount > 0 ? errors.cadence : undefined}
                      />
                    </Stack>

                    {needsStartDay(values.cadence) && (
                      <Stack gap="xs" fullWidth>
                        <AutoComplete
                          label="First day of the pay period"
                          placeholder="Select a day"
                          required
                          selectedValue={values.startDayOfWeek}
                          onSelectedValueChange={(value) =>
                            setFieldValue("startDayOfWeek", value)
                          }
                          searchValue={startDaySearch}
                          onSearchValueChange={setStartDaySearch}
                          error={
                            submitCount > 0 ? errors.startDayOfWeek : undefined
                          }
                          items={START_DAY_OF_WEEK_OPTIONS}
                          fullWidth
                        />
                        <FieldError
                          message={
                            submitCount > 0 ? errors.startDayOfWeek : undefined
                          }
                        />
                      </Stack>
                    )}

                    {needsReferenceDate(values.cadence) && (
                      <Stack gap="xs" fullWidth>
                        <TypeableDatePicker
                          name="periodStartReferenceDate"
                          label="Pay period start reference date"
                          value={values.periodStartReferenceDate}
                          onChange={(date) =>
                            setFieldValue("periodStartReferenceDate", date)
                          }
                          error={
                            submitCount > 0
                              ? errors.periodStartReferenceDate
                              : undefined
                          }
                          fullWidth
                        />
                        <FieldError
                          message={
                            submitCount > 0
                              ? errors.periodStartReferenceDate
                              : undefined
                          }
                        />
                        <Typography variant="captionW1" textColor="tertiary">
                          Pick a date you know one of your two-week pay periods
                          started. It tells us which weeks are on.
                        </Typography>
                      </Stack>
                    )}

                    {/* The banner is only for a save that failed: every validation
                          message sits on its own field. */}
                    {status && <Alert variant="destructive">{status}</Alert>}
                  </Stack>
                </CardContent>
              </Card>

              {/* Grouped bottom-right: two buttons pushed to opposite edges read as two
                  stranded controls, a pair reads as one decision. */}
              <Stack
                direction="row"
                gap="sm"
                justify="end"
                align="center"
                fullWidth
              >
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  disabled={isSubmitting}
                  onClick={onBack}
                >
                  Back
                </Button>
                <Button type="submit" size="sm" loading={isSubmitting}>
                  Continue
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </StepLayout>
  );
};
